import type { Geometry, GeomPath, PathCmd } from "@deckloom/core/schema";

/** Presets whose path is stroked only (the spec marks their paths fill="none"), even when the shape has a fill. */
export const STROKE_ONLY_PRESETS = new Set([
  "arc", "leftBrace", "rightBrace", "bracePair", "leftBracket", "rightBracket", "bracketPair",
  "line", "straightConnector1", "bentConnector2", "bentConnector3", "curvedConnector2", "curvedConnector3",
]);

/**
 * SVG path data for a geometry at a given size. Preset shapes are a hand-written subset of the ECMA-376
 * preset table (guide formulas simplified to their defaults where the adjustments are rarely used).
 */
export function geometryPath(geom: Geometry | undefined, w: number, h: number): string {
  if (!geom) return rect(w, h);
  if (geom.kind === "custom") return geom.paths.map((p) => customPath(p, w, h)).join(" ");
  const adj = geom.adjust ?? {};
  const ss = Math.min(w, h);
  const a = (name: string, def: number) => adj[name] ?? def;
  /** adjustment as a length, as a fraction of the shorter side */
  const al = (name: string, def: number) => (ss * a(name, def)) / 100000;
  switch (geom.preset) {
    case "rect":
    case "flowChartProcess":
      return rect(w, h);
    case "roundRect":
    case "flowChartAlternateProcess":
      return roundRect(w, h, al("adj", 16667));
    case "round1Rect": {
      const r = clampR(al("adj", 16667), w, h);
      return `M0,0 L${w - r},0 A${r},${r} 0 0 1 ${w},${r} L${w},${h} L0,${h} Z`;
    }
    case "round2SameRect": {
      const r1 = clampR(al("adj1", 16667), w, h), r2 = clampR(al("adj2", 0), w, h);
      return `M${r1},0 L${w - r1},0 A${r1},${r1} 0 0 1 ${w},${r1} L${w},${h - r2} A${r2},${r2} 0 0 1 ${w - r2},${h} L${r2},${h} A${r2},${r2} 0 0 1 0,${h - r2} L0,${r1} A${r1},${r1} 0 0 1 ${r1},0 Z`;
    }
    case "round2DiagRect": {
      const r1 = clampR(al("adj1", 16667), w, h), r2 = clampR(al("adj2", 0), w, h);
      return `M${r1},0 L${w - r2},0 A${r2},${r2} 0 0 1 ${w},${r2} L${w},${h - r1} A${r1},${r1} 0 0 1 ${w - r1},${h} L${r2},${h} A${r2},${r2} 0 0 1 0,${h - r2} L0,${r1} A${r1},${r1} 0 0 1 ${r1},0 Z`;
    }
    case "snipRoundRect": {
      const r = clampR(al("adj1", 16667), w, h), d = clampR(al("adj2", 16667), w, h);
      return `M${r},0 L${w - d},0 L${w},${d} L${w},${h} L0,${h} L0,${r} A${r},${r} 0 0 1 ${r},0 Z`;
    }
    case "snip1Rect": {
      const d = clampR(al("adj", 16667), w, h);
      return `M0,0 L${w - d},0 L${w},${d} L${w},${h} L0,${h} Z`;
    }
    case "snip2SameRect": {
      const d1 = clampR(al("adj1", 16667), w, h), d2 = clampR(al("adj2", 0), w, h);
      return `M${d1},0 L${w - d1},0 L${w},${d1} L${w},${h - d2} L${w - d2},${h} L${d2},${h} L0,${h - d2} L0,${d1} Z`;
    }
    case "snip2DiagRect": {
      const d1 = clampR(al("adj1", 0), w, h), d2 = clampR(al("adj2", 16667), w, h);
      return `M${d1},0 L${w - d2},0 L${w},${d2} L${w},${h - d1} L${w - d1},${h} L${d2},${h} L0,${h - d2} L0,${d1} Z`;
    }
    case "ellipse":
    case "flowChartConnector":
      return ellipse(w, h);
    case "triangle": {
      const x = (w * a("adj", 50000)) / 100000;
      return `M${x},0 L${w},${h} L0,${h} Z`;
    }
    case "rtTriangle":
      return `M0,0 L${w},${h} L0,${h} Z`;
    case "diamond":
    case "flowChartDecision":
      return `M${w / 2},0 L${w},${h / 2} L${w / 2},${h} L0,${h / 2} Z`;
    case "parallelogram": {
      const d = al("adj", 25000);
      return `M${d},0 L${w},0 L${w - d},${h} L0,${h} Z`;
    }
    case "flowChartInputOutput":
      return `M${w * 0.2},0 L${w},0 L${w * 0.8},${h} L0,${h} Z`;
    case "trapezoid": {
      const d = al("adj", 25000);
      return `M${d},0 L${w - d},0 L${w},${h} L0,${h} Z`;
    }
    case "nonIsoscelesTrapezoid": {
      const d1 = al("adj1", 25000), d2 = al("adj2", 25000);
      return `M${d1},0 L${w - d2},0 L${w},${h} L0,${h} Z`;
    }
    case "flowChartPreparation":
      return `M${w * 0.2},0 L${w * 0.8},0 L${w},${h / 2} L${w * 0.8},${h} L${w * 0.2},${h} L0,${h / 2} Z`;
    case "flowChartOffpageConnector":
      return `M0,0 L${w},0 L${w},${h * 0.8} L${w / 2},${h} L0,${h * 0.8} Z`;
    case "flowChartMagneticDisk": {
      const ry = h * 0.125;
      return `M0,${ry} A${w / 2},${ry} 0 0 1 ${w},${ry} L${w},${h - ry} A${w / 2},${ry} 0 0 1 0,${h - ry} Z M0,${ry} A${w / 2},${ry} 0 0 0 ${w},${ry}`;
    }
    case "pentagon":
      return polygon(w, h, 5, -90);
    case "hexagon":
      return polygon(w, h, 6, 0);
    case "heptagon":
      return polygon(w, h, 7, -90);
    case "octagon":
      return polygon(w, h, 8, 22.5);
    case "decagon":
      return polygon(w, h, 10, 0);
    case "dodecagon":
      return polygon(w, h, 12, 15);
    case "line":
    case "straightConnector1":
      return `M0,0 L${w},${h}`;
    case "bentConnector2":
      return `M0,0 L${w},0 L${w},${h}`;
    case "bentConnector3": {
      const mx = (w * a("adj1", 50000)) / 100000;
      return `M0,0 L${mx},0 L${mx},${h} L${w},${h}`;
    }
    case "curvedConnector2":
      return `M0,0 C${w / 2},0 ${w},${h / 2} ${w},${h}`;
    case "curvedConnector3": {
      const mx = (w * a("adj1", 50000)) / 100000;
      return `M0,0 C${mx},0 ${mx},${h} ${w},${h}`;
    }
    case "rightArrow":
      return arrow(w, h, adj, 0);
    case "leftArrow":
      return arrow(w, h, adj, 180);
    case "upArrow":
      return arrow(w, h, adj, 270);
    case "downArrow":
      return arrow(w, h, adj, 90);
    case "notchedRightArrow": {
      const shaft = (h * a("adj1", 50000)) / 100000, head = al("adj2", 50000), t = (h - shaft) / 2;
      const notch = (head * shaft) / h;
      return `M0,${t} L${w - head},${t} L${w - head},0 L${w},${h / 2} L${w - head},${h} L${w - head},${h - t} L0,${h - t} L${notch},${h / 2} Z`;
    }
    case "stripedRightArrow": {
      const shaft = (h * a("adj1", 50000)) / 100000, head = al("adj2", 50000), t = (h - shaft) / 2;
      const s1 = ss / 32, s2 = ss / 8, s3 = ss / 4;
      return `M0,${t} L${s1},${t} L${s1},${h - t} L0,${h - t} Z M${s2},${t} L${s3},${t} L${s3},${h - t} L${s2},${h - t} Z M${s3 + s1 * 2},${t} L${w - head},${t} L${w - head},0 L${w},${h / 2} L${w - head},${h} L${w - head},${h - t} L${s3 + s1 * 2},${h - t} Z`;
    }
    case "leftRightArrow": {
      const hw = (h * a("adj1", 50000)) / 100000;
      const hl = al("adj2", 50000);
      const t = (h - hw) / 2;
      return `M0,${h / 2} L${hl},0 L${hl},${t} L${w - hl},${t} L${w - hl},0 L${w},${h / 2} L${w - hl},${h} L${w - hl},${h - t} L${hl},${h - t} L${hl},${h} Z`;
    }
    case "upDownArrow": {
      const sw = (w * a("adj1", 50000)) / 100000;
      const hl = al("adj2", 50000);
      const t = (w - sw) / 2;
      return `M${w / 2},0 L${w},${hl} L${w - t},${hl} L${w - t},${h - hl} L${w},${h - hl} L${w / 2},${h} L0,${h - hl} L${t},${h - hl} L${t},${hl} L0,${hl} Z`;
    }
    case "quadArrow":
    case "leftRightUpArrow": {
      const sw = al("adj1", 22500), hw = al("adj2", 22500), hl = al("adj3", 22500);
      const cx = w / 2, cy = h / 2, s = sw / 2, hh = hw;
      const up = `M${cx},0 L${cx + hh},${hl} L${cx + s},${hl} L${cx + s},${cy - s}`;
      const right = ` L${w - hl},${cy - s} L${w - hl},${cy - hh} L${w},${cy} L${w - hl},${cy + hh} L${w - hl},${cy + s} L${cx + s},${cy + s}`;
      const down = geom.preset === "quadArrow"
        ? ` L${cx + s},${h - hl} L${cx + hh},${h - hl} L${cx},${h} L${cx - hh},${h - hl} L${cx - s},${h - hl} L${cx - s},${cy + s}`
        : ` L${cx + s},${h} L${cx - s},${h} L${cx - s},${cy + s}`;
      const left = ` L${hl},${cy + s} L${hl},${cy + hh} L0,${cy} L${hl},${cy - hh} L${hl},${cy - s} L${cx - s},${cy - s} L${cx - s},${hl} L${cx - hh},${hl} Z`;
      return up + right + down + left;
    }
    case "chevron":
    case "homePlate": {
      const d = al("adj", 50000);
      const notch = geom.preset === "chevron" ? d : 0;
      return `M0,0 L${w - d},0 L${w},${h / 2} L${w - d},${h} L0,${h} L${notch},${h / 2} Z`;
    }
    case "star4":
      return star(w, h, 4, a("adj", 12500));
    case "star5":
      return star(w, h, 5, a("adj", 19098));
    case "star6":
      return star(w, h, 6, a("adj", 28868));
    case "star7":
      return star(w, h, 7, a("adj", 34601));
    case "star8":
      return star(w, h, 8, a("adj", 37500));
    case "star10":
      return star(w, h, 10, a("adj", 42533));
    case "star12":
      return star(w, h, 12, a("adj", 37500));
    case "star16":
      return star(w, h, 16, a("adj", 37500));
    case "star24":
      return star(w, h, 24, a("adj", 37500));
    case "star32":
      return star(w, h, 32, a("adj", 37500));
    case "sun": {
      const r = (ss * a("adj", 25000)) / 100000;
      const inner = ellipseAt(w / 2 - r, h / 2 - r, r * 2, r * 2, false);
      let rays = "";
      for (let i = 0; i < 8; i++) {
        const ang = (i * Math.PI) / 4, c = Math.cos(ang), s = Math.sin(ang);
        const tipX = w / 2 + (w / 2) * c, tipY = h / 2 + (h / 2) * s;
        const bx = w / 2 + (r * 1.15) * c, by = h / 2 + (r * 1.15) * s;
        const px = -s * r * 0.35, py = c * r * 0.35;
        rays += ` M${bx + px},${by + py} L${tipX},${tipY} L${bx - px},${by - py} Z`;
      }
      return inner + rays;
    }
    case "moon": {
      const d = (w * a("adj", 50000)) / 100000;
      return `M${w},0 A${w},${h / 2} 0 0 0 ${w},${h} A${w - d},${h / 2} 0 0 1 ${w},0 Z`;
    }
    case "flowChartTerminator":
      return roundRect(w, h, h / 2);
    case "flowChartDocument":
      return `M0,0 L${w},0 L${w},${h * 0.8} Q${w * 0.75},${h * 0.6} ${w / 2},${h * 0.8} Q${w * 0.25},${h} 0,${h * 0.85} Z`;
    case "wedgeRectCallout":
    case "wedgeRoundRectCallout": {
      const body = geom.preset === "wedgeRoundRectCallout" ? roundRect(w, h, al("adj3", 16667)) : rect(w, h);
      return body + " " + calloutTail(w, h, a("adj1", -20833), a("adj2", 62500), false);
    }
    case "wedgeEllipseCallout":
      return ellipse(w, h) + " " + calloutTail(w, h, a("adj1", -20833), a("adj2", 62500), true);
    case "plus":
    case "mathPlus": {
      const d = geom.preset === "plus" ? al("adj", 25000) : (ss * (100000 - a("adj1", 23520))) / 200000;
      return `M${d},0 L${w - d},0 L${w - d},${d} L${w},${d} L${w},${h - d} L${w - d},${h - d} L${w - d},${h} L${d},${h} L${d},${h - d} L0,${h - d} L0,${d} L${d},${d} Z`;
    }
    case "mathMinus": {
      const t = (h * a("adj1", 23520)) / 100000;
      return `M${w * 0.1},${h / 2 - t / 2} L${w * 0.9},${h / 2 - t / 2} L${w * 0.9},${h / 2 + t / 2} L${w * 0.1},${h / 2 + t / 2} Z`;
    }
    case "mathEqual": {
      const t = (h * a("adj1", 23520)) / 100000, gap = (h * a("adj2", 11760)) / 100000;
      const y1 = h / 2 - gap / 2 - t, y2 = h / 2 + gap / 2;
      return `M${w * 0.1},${y1} L${w * 0.9},${y1} L${w * 0.9},${y1 + t} L${w * 0.1},${y1 + t} Z M${w * 0.1},${y2} L${w * 0.9},${y2} L${w * 0.9},${y2 + t} L${w * 0.1},${y2 + t} Z`;
    }
    case "mathMultiply": {
      const t = (ss * a("adj1", 23520)) / 100000;
      const pts = crossPoints(w, h, t);
      return `M${pts.join(" L")} Z`;
    }
    case "heart":
      return `M${w / 2},${h} C${-w * 0.2},${h * 0.5} ${w * 0.1},${-h * 0.15} ${w / 2},${h * 0.25} C${w * 0.9},${-h * 0.15} ${w * 1.2},${h * 0.5} ${w / 2},${h} Z`;
    case "cloud":
      return cloud(w, h);
    case "donut": {
      const t = al("adj", 25000);
      return `${ellipse(w, h)} ${ellipseAt(t, t, w - 2 * t, h - 2 * t, true)}`;
    }
    case "noSmoking": {
      const t = al("adj", 18750);
      return `${ellipse(w, h)} ${ellipseAt(t, t, w - 2 * t, h - 2 * t, true)} M${w * 0.25},${h / 2 - t / 2} L${w * 0.75},${h / 2 - t / 2} L${w * 0.75},${h / 2 + t / 2} L${w * 0.25},${h / 2 + t / 2} Z`;
    }
    case "blockArc": {
      const st = a("adj1", 10800000) / 60000, en = a("adj2", 0) / 60000, dr = al("adj3", 25000);
      return ring(w, h, st, en, dr);
    }
    case "chord": {
      const st = a("adj1", 2700000) / 60000, en = a("adj2", 16200000) / 60000;
      const { p1, p2, large } = arcPoints(w, h, st, en);
      return `M${p1} A${w / 2},${h / 2} 0 ${large} 1 ${p2} Z`;
    }
    case "pie": {
      const st = a("adj1", 0) / 60000, en = a("adj2", 16200000) / 60000;
      const { p1, p2, large } = arcPoints(w, h, st, en);
      return `M${w / 2},${h / 2} L${p1} A${w / 2},${h / 2} 0 ${large} 1 ${p2} Z`;
    }
    case "arc": {
      const st = a("adj1", 16200000) / 60000, en = a("adj2", 0) / 60000;
      const { p1, p2, large } = arcPoints(w, h, st, en);
      return `M${p1} A${w / 2},${h / 2} 0 ${large} 1 ${p2}`;
    }
    case "teardrop": {
      const rx = w / 2, ry = h / 2;
      const ext = (a("adj", 100000) / 100000) * 0.5;
      return `M0,${ry} A${rx},${ry} 0 0 1 ${rx},0 L${w},0 L${w},${h * (0.5 - ext * 0.5)} A${rx},${ry} 0 0 1 ${rx},${h} A${rx},${ry} 0 0 1 0,${ry} Z`;
    }
    case "frame": {
      const d = al("adj1", 12500);
      return `${rect(w, h)} M${d},${d} L${d},${h - d} L${w - d},${h - d} L${w - d},${d} Z`;
    }
    case "halfFrame": {
      const d1 = al("adj1", 33333), d2 = al("adj2", 33333);
      return `M0,0 L${w},0 L${w - d2 * (w / h)},${d1} L${d2},${d1} L${d2},${h - d1 * (h / w)} L0,${h} Z`;
    }
    case "corner": {
      const d1 = al("adj1", 50000), d2 = al("adj2", 50000);
      return `M0,0 L${d2},0 L${d2},${h - d1} L${w},${h - d1} L${w},${h} L0,${h} Z`;
    }
    case "diagStripe": {
      const d = al("adj", 50000);
      return `M0,${d} L${d},0 L${w},0 L0,${h} Z`;
    }
    case "bevel": {
      const d = al("adj", 12500);
      return `${rect(w, h)} M${d},${d} L${w - d},${d} L${w - d},${h - d} L${d},${h - d} Z M0,0 L${d},${d} M${w},0 L${w - d},${d} M${w},${h} L${w - d},${h - d} M0,${h} L${d},${h - d}`;
    }
    case "cube": {
      const d = al("adj", 25000);
      return `M0,${d} L${d},0 L${w},0 L${w},${h - d} L${w - d},${h} L0,${h} Z M0,${d} L${w - d},${d} L${w},0 M${w - d},${d} L${w - d},${h}`;
    }
    case "foldedCorner": {
      const d = al("adj", 16667);
      return `M0,0 L${w},0 L${w},${h - d} L${w - d},${h} L0,${h} Z M${w - d},${h} L${w - d * 0.8},${h - d * 0.8} L${w},${h - d}`;
    }
    case "lightningBolt":
      return scaled(w, h, "M0.4,0 L0.65,0.4 L0.55,0.42 L0.8,0.7 L0.68,0.72 L1,1 L0.5,0.68 L0.6,0.65 L0.3,0.45 L0.42,0.42 Z");
    case "ribbon":
    case "ribbon2": {
      const d = al("adj1", 16667);
      const y0 = geom.preset === "ribbon" ? 0 : h * 0.3, y1 = geom.preset === "ribbon" ? h * 0.7 : h;
      return `M0,${y0} L${w * 0.15},${y0} L${w * 0.15},${(y0 + y1) / 2 - d / 2} L${w * 0.85},${(y0 + y1) / 2 - d / 2} L${w * 0.85},${y0} L${w},${y0} L${w * 0.92},${(y0 + y1) / 2} L${w},${y1} L${w * 0.85},${y1} L${w * 0.85},${(y0 + y1) / 2 + d / 2} L${w * 0.15},${(y0 + y1) / 2 + d / 2} L${w * 0.15},${y1} L0,${y1} L${w * 0.08},${(y0 + y1) / 2} Z`;
    }
    case "ellipseRibbon":
      return scaled(w, h, "M0,0 L0.2,0 Q0.5,0.25 0.8,0 L1,0 L0.9,0.45 L1,0.9 L0.8,0.9 Q0.5,1.1 0.2,0.9 L0,0.9 L0.1,0.45 Z");
    case "verticalScroll": {
      const d = al("adj", 12500);
      return `${roundRect(w - d, h - d, d / 2)} M${d / 2},${d / 2} L${d / 2},${h} L${w},${h} L${w},${d}`;
    }
    case "horizontalScroll": {
      const d = al("adj", 12500);
      return `M${d},0 L${w},0 L${w},${h - d} L${w - d},${h - d} L${w - d},${h} L0,${h} L0,${d} Z M${d},0 L${d},${h - d} L${w - d},${h - d}`;
    }
    case "wave":
    case "doubleWave": {
      const amp = (h * a("adj1", 12500)) / 100000;
      const n = geom.preset === "wave" ? 1 : 2, seg = w / n;
      let d = `M0,${amp}`;
      for (let i = 0; i < n; i++) d += ` Q${i * seg + seg / 4},${-amp * 0.5} ${i * seg + seg / 2},${amp} Q${i * seg + (3 * seg) / 4},${amp * 2.5} ${(i + 1) * seg},${amp}`;
      d += ` L${w},${h - amp}`;
      for (let i = n - 1; i >= 0; i--) d += ` Q${i * seg + (3 * seg) / 4},${h - amp * 2.5} ${i * seg + seg / 2},${h - amp} Q${i * seg + seg / 4},${h + amp * 0.5} ${i * seg},${h - amp}`;
      return d + " Z";
    }
    case "bracketPair": {
      const r = al("adj", 16667);
      return `M${r},0 A${r},${r} 0 0 0 0,${r} L0,${h - r} A${r},${r} 0 0 0 ${r},${h} M${w - r},0 A${r},${r} 0 0 1 ${w},${r} L${w},${h - r} A${r},${r} 0 0 1 ${w - r},${h}`;
    }
    case "leftBracket": {
      const r = Math.min(al("adj", 8333), w);
      return `M${w},0 L${r},0 A${r},${r} 0 0 0 0,${r} L0,${h - r} A${r},${r} 0 0 0 ${r},${h} L${w},${h}`;
    }
    case "rightBracket": {
      const r = Math.min(al("adj", 8333), w);
      return `M0,0 L${w - r},0 A${r},${r} 0 0 1 ${w},${r} L${w},${h - r} A${r},${r} 0 0 1 ${w - r},${h} L0,${h}`;
    }
    case "bracePair": {
      const r = al("adj", 8333);
      return brace(0, r, r * 2, r, h / 2, h) + " " + brace(w, w - r, w - r * 2, r, h / 2, h);
    }
    case "leftBrace":
    case "rightBrace": {
      const r = Math.min(al("adj1", 8333), w / 2, h / 4);
      const y = (h * a("adj2", 50000)) / 100000;
      return geom.preset === "leftBrace" ? brace(0, w / 2, w, r, y, h) : brace(w, w / 2, 0, r, y, h);
    }
    case "can": {
      const ry = (ss * a("adj", 25000)) / 100000 / 2;
      return `M0,${ry} A${w / 2},${ry} 0 0 1 ${w},${ry} L${w},${h - ry} A${w / 2},${ry} 0 0 1 0,${h - ry} Z M0,${ry} A${w / 2},${ry} 0 0 0 ${w},${ry}`;
    }
    default:
      return rect(w, h);
  }
}

const rect = (w: number, h: number) => `M0,0 L${w},0 L${w},${h} L0,${h} Z`;
const clampR = (r: number, w: number, h: number) => Math.max(0, Math.min(r, w / 2, h / 2));
const roundRect = (w: number, h: number, r: number) => {
  r = clampR(r, w, h);
  if (r <= 0) return rect(w, h);
  return `M${r},0 L${w - r},0 A${r},${r} 0 0 1 ${w},${r} L${w},${h - r} A${r},${r} 0 0 1 ${w - r},${h} L${r},${h} A${r},${r} 0 0 1 0,${h - r} L0,${r} A${r},${r} 0 0 1 ${r},0 Z`;
};
const ellipse = (w: number, h: number) => ellipseAt(0, 0, w, h, false);
function ellipseAt(x: number, y: number, w: number, h: number, reverse: boolean): string {
  const rx = w / 2, ry = h / 2, cx = x + rx, cy = y + ry;
  const sweep = reverse ? 0 : 1;
  return `M${cx - rx},${cy} A${rx},${ry} 0 1 ${sweep} ${cx + rx},${cy} A${rx},${ry} 0 1 ${sweep} ${cx - rx},${cy} Z`;
}
/** Scale a path written in a unit square to w x h. */
function scaled(w: number, h: number, unit: string): string {
  return unit.replace(/(-?[\d.]+),(-?[\d.]+)/g, (_, x: string, y: string) => `${Number(x) * w},${Number(y) * h}`);
}
function polygon(w: number, h: number, n: number, startDeg: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const a = ((startDeg + (360 / n) * i) * Math.PI) / 180;
    pts.push(`${w / 2 + (w / 2) * Math.cos(a)},${h / 2 + (h / 2) * Math.sin(a)}`);
  }
  return `M${pts.join(" L")} Z`;
}
function star(w: number, h: number, n: number, innerAdj: number): string {
  const inner = innerAdj / 50000;
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? 1 : inner;
    const a = (-90 + (180 / n) * i) * (Math.PI / 180);
    pts.push(`${w / 2 + (w / 2) * r * Math.cos(a)},${h / 2 + (h / 2) * r * Math.sin(a)}`);
  }
  return `M${pts.join(" L")} Z`;
}
function arrow(w: number, h: number, adj: Record<string, number>, rotDeg: number): string {
  // Build a right arrow in a (W,H) box then rotate about center for the other directions.
  const vertical = rotDeg === 90 || rotDeg === 270;
  const W = vertical ? h : w, H = vertical ? w : h;
  const shaft = (H * (adj.adj1 ?? 50000)) / 100000;
  const head = (Math.min(W, H) * (adj.adj2 ?? 50000)) / 100000;
  const t = (H - shaft) / 2;
  const pts: [number, number][] = [[0, t], [W - head, t], [W - head, 0], [W, H / 2], [W - head, H], [W - head, H - t], [0, H - t]];
  const rad = (rotDeg * Math.PI) / 180, cos = Math.cos(rad), sin = Math.sin(rad);
  const out = pts.map(([x, y]) => {
    const dx = x - W / 2, dy = y - H / 2;
    return `${w / 2 + dx * cos - dy * sin},${h / 2 + dx * sin + dy * cos}`;
  });
  return `M${out.join(" L")} Z`;
}
function cloud(w: number, h: number): string {
  const b = (x: number, y: number) => `${x * w},${y * h}`;
  return `M${b(0.2, 0.75)} C${b(0.05, 0.75)} ${b(0, 0.5)} ${b(0.15, 0.4)} C${b(0.1, 0.2)} ${b(0.3, 0.1)} ${b(0.4, 0.2)} C${b(0.5, 0.0)} ${b(0.75, 0.05)} ${b(0.75, 0.25)} C${b(0.95, 0.25)} ${b(1, 0.5)} ${b(0.85, 0.6)} C${b(1, 0.8)} ${b(0.8, 0.95)} ${b(0.65, 0.85)} C${b(0.55, 1)} ${b(0.3, 1)} ${b(0.2, 0.75)} Z`;
}
/** Points on the bounding ellipse at OOXML angles (degrees, clockwise from +x), plus the large-arc flag. */
function arcPoints(w: number, h: number, stDeg: number, enDeg: number) {
  const rx = w / 2, ry = h / 2;
  const pt = (deg: number) => {
    const r = (deg * Math.PI) / 180;
    return `${rx + rx * Math.cos(r)},${ry + ry * Math.sin(r)}`;
  };
  let sweep = (((enDeg - stDeg) % 360) + 360) % 360;
  if (sweep === 0) sweep = 359.99;
  return { p1: pt(stDeg), p2: pt(stDeg + sweep), large: sweep > 180 ? 1 : 0, sweep };
}
function ring(w: number, h: number, stDeg: number, enDeg: number, dr: number): string {
  const rx = w / 2, ry = h / 2, irx = Math.max(0, rx - dr), iry = Math.max(0, ry - dr);
  const { sweep, large } = arcPoints(w, h, stDeg, enDeg);
  const pt = (r1: number, r2: number, deg: number) => {
    const r = (deg * Math.PI) / 180;
    return `${rx + r1 * Math.cos(r)},${ry + r2 * Math.sin(r)}`;
  };
  const en = stDeg + sweep;
  return `M${pt(rx, ry, stDeg)} A${rx},${ry} 0 ${large} 1 ${pt(rx, ry, en)} L${pt(irx, iry, en)} A${irx},${iry} 0 ${large} 0 ${pt(irx, iry, stDeg)} Z`;
}
/** A curly brace: ends at x=endX, vertical bar at x=barX, point at (tipX, y). */
function brace(tipX: number, barX: number, endX: number, r: number, y: number, h: number): string {
  y = Math.max(r * 2, Math.min(h - r * 2, y));
  return `M${endX},0 Q${barX},0 ${barX},${r} L${barX},${y - r} Q${barX},${y} ${tipX},${y} Q${barX},${y} ${barX},${y + r} L${barX},${h - r} Q${barX},${h} ${endX},${h}`;
}
/** Callout tail: a triangle from the body edge (or ellipse rim) to the tip given as offsets from the center (per 100000). */
function calloutTail(w: number, h: number, adj1: number, adj2: number, onEllipse: boolean): string {
  const tx = w / 2 + (w * adj1) / 100000, ty = h / 2 + (h * adj2) / 100000;
  const dx = tx - w / 2, dy = ty - h / 2;
  if (Math.abs(dx) <= w / 2 && Math.abs(dy) <= h / 2 && !onEllipse) return "";
  const bw = Math.min(w, h) / 4;
  if (onEllipse) {
    const ang = Math.atan2(dy / (h / 2), dx / (w / 2));
    const da = 0.35;
    const p = (a: number) => `${w / 2 + (w / 2) * Math.cos(a) * 0.95},${h / 2 + (h / 2) * Math.sin(a) * 0.95}`;
    return `M${p(ang - da)} L${tx},${ty} L${p(ang + da)} Z`;
  }
  // which edge the tail leaves from
  if (Math.abs(dy) * w >= Math.abs(dx) * h) {
    const ey = dy > 0 ? h : 0;
    const bx = Math.max(bw, Math.min(w - bw, w / 2 + dx * 0.5));
    return `M${bx - bw / 2},${ey} L${tx},${ty} L${bx + bw / 2},${ey} Z`;
  }
  const ex = dx > 0 ? w : 0;
  const by = Math.max(bw, Math.min(h - bw, h / 2 + dy * 0.5));
  return `M${ex},${by - bw / 2} L${tx},${ty} L${ex},${by + bw / 2} Z`;
}
function crossPoints(w: number, h: number, t: number): string[] {
  const cx = w / 2, cy = h / 2, ang = Math.atan2(h, w), c = Math.cos(ang), s = Math.sin(ang);
  const L = Math.hypot(w, h) / 2 * 0.9, k = t / 2;
  const pts: [number, number][] = [];
  const push = (dx: number, dy: number) => pts.push([cx + dx, cy + dy]);
  // 12-point cross built from the two diagonals
  const d1 = [c, s], d2 = [c, -s];
  push(-d1[0]! * L + -d1[1]! * k, -d1[1]! * L + d1[0]! * k); push(-d1[0]! * L - -d1[1]! * k, -d1[1]! * L - d1[0]! * k);
  push(-d2[1]! * k, d2[0]! * k); push(d2[0]! * L - -d2[1]! * k, d2[1]! * L - d2[0]! * k); push(d2[0]! * L + -d2[1]! * k, d2[1]! * L + d2[0]! * k);
  push(d1[1]! * k, -d1[0]! * k); push(d1[0]! * L + -d1[1]! * k, d1[1]! * L + d1[0]! * k); push(d1[0]! * L - -d1[1]! * k, d1[1]! * L - d1[0]! * k);
  push(d2[1]! * k, -d2[0]! * k); push(-d2[0]! * L - -d2[1]! * k, -d2[1]! * L - d2[0]! * k); push(-d2[0]! * L + -d2[1]! * k, -d2[1]! * L + d2[0]! * k);
  push(-d1[1]! * k, d1[0]! * k);
  return pts.map(([x, y]) => `${x},${y}`);
}

function customPath(p: GeomPath, w: number, h: number): string {
  const sx = p.w ? w / p.w : 1, sy = p.h ? h / p.h : 1;
  let cx = 0, cy = 0;
  const parts: string[] = [];
  for (const c of p.cmds) parts.push(cmd(c));
  return parts.join(" ");

  function cmd(c: PathCmd): string {
    switch (c.c) {
      case "M": cx = c.x; cy = c.y; return `M${c.x * sx},${c.y * sy}`;
      case "L": cx = c.x; cy = c.y; return `L${c.x * sx},${c.y * sy}`;
      case "C": cx = c.x; cy = c.y; return `C${c.x1 * sx},${c.y1 * sy} ${c.x2 * sx},${c.y2 * sy} ${c.x * sx},${c.y * sy}`;
      case "Q": cx = c.x; cy = c.y; return `Q${c.x1 * sx},${c.y1 * sy} ${c.x * sx},${c.y * sy}`;
      case "A": {
        // a:arcTo: current point lies on the ellipse at stAng; sweep swAng degrees.
        const st = (c.stAng * Math.PI) / 180, sw = (c.swAng * Math.PI) / 180;
        const ccx = cx - c.wR * Math.cos(st), ccy = cy - c.hR * Math.sin(st);
        const ex = ccx + c.wR * Math.cos(st + sw), ey = ccy + c.hR * Math.sin(st + sw);
        const large = Math.abs(c.swAng) > 180 ? 1 : 0, sweep = c.swAng > 0 ? 1 : 0;
        cx = ex; cy = ey;
        return `A${c.wR * sx},${c.hR * sy} 0 ${large} ${sweep} ${ex * sx},${ey * sy}`;
      }
      case "Z": return "Z";
    }
  }
}
